import type { EmphasisLevel, RoleDraft } from './types';

const asKey = (skill: string) => skill.trim().toLowerCase();

/** A skill's level in the draft, normal when it has none. */
export const emphasisOf = (
  emphasis: Record<string, EmphasisLevel>,
  skill: string,
): EmphasisLevel | 'Normal' =>
  Object.entries(emphasis).find(([key]) => asKey(key) === asKey(skill))?.[1] ??
  'Normal';

/** The draft's emphasis with one skill's level set; normal removes it. */
export const withEmphasis = (
  emphasis: Record<string, EmphasisLevel>,
  skill: string,
  level: EmphasisLevel | 'Normal',
): Record<string, EmphasisLevel> => {
  const others = Object.fromEntries(
    Object.entries(emphasis).filter(([key]) => asKey(key) !== asKey(skill)),
  );
  return level === 'Normal' ? others : { ...others, [skill]: level };
};

/** The emphasis of the skills the draft still lists, keyed as the lists write them: finlay-api refuses any other. */
export const emphasisForRequest = ({
  mustHave,
  niceToHave,
  skillEmphasis,
}: Pick<RoleDraft, 'mustHave' | 'niceToHave' | 'skillEmphasis'>): Record<
  string,
  EmphasisLevel
> => {
  const listed = new Map(
    [...mustHave, ...niceToHave].map((skill) => [asKey(skill), skill]),
  );
  return Object.fromEntries(
    Object.entries(skillEmphasis).flatMap(([skill, level]) => {
      const asWritten = listed.get(asKey(skill));
      return asWritten ? [[asWritten, level]] : [];
    }),
  );
};

/** A saved job's emphasis as the draft keeps it: the API sends no normal levels, and the draft has none. */
export const savedEmphasis = (
  emphasis: Record<string, EmphasisLevel | 'Normal'>,
): Record<string, EmphasisLevel> =>
  Object.fromEntries(
    Object.entries(emphasis).filter(
      (entry): entry is [string, EmphasisLevel] => entry[1] !== 'Normal',
    ),
  );
